/**
 * StandardsProfilesPage - Manage Standards Delivery Profiles
 *
 * Profiles tune standards delivery PRIORITY, not applicability.
 * Foundation standards (priority 10) are ALWAYS included regardless of profile.
 * Critical alerts CANNOT be suppressed.
 *
 * Features:
 * - List view: Table of existing profiles with status, effectiveness, usage stats
 * - Create/Edit modal: Form matching DB schema (migration 037)
 * - Preview panel: Summary of profile impact on standards delivery
 * - Constraint display: Foundation standards cannot be deprioritized
 * - Activate/deactivate toggle
 * - Delete with confirmation
 */

import React, { useCallback, useEffect, useState } from 'react';
import { FiPlus, FiRefreshCw, FiEdit2, FiTrash2, FiEye, FiX, FiAlertTriangle, FiShield, FiGrid, FiList } from 'react-icons/fi';
import { useStatus } from '../contexts/StatusProvider';
import { isSuccessResponse } from '../api/apiClient';
import {
  listProfiles,
  getProfile,
  createProfile,
  updateProfile,
  deleteProfile,
  previewProfile,
  getStatusBadgeColor,
  formatEffectivenessScore,
  formatSuccessRate,
  STANDARD_CATEGORIES,
  type StandardsProfile,
  type CreateProfileInput,
  type UpdateProfileInput,
  type ProfilePreview,
} from '../api/standardsProfiles';
import ProfileBuilder from './StandardsProfiles/ProfileBuilder';
import ProfileSimulator from './StandardsProfiles/ProfileSimulator';

// ─── Sub-components ──────────────────────────────────────────

/** Status badge for profile status */
const StatusBadge: React.FC<{ status: string }> = ({ status }) => (
  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeColor(status)}`}>
    {status.charAt(0).toUpperCase() + status.slice(1)}
  </span>
);

/** Foundation constraint banner */
const FoundationConstraintBanner: React.FC = () => (
  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start space-x-3">
    <FiShield className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
    <div>
      <p className="text-sm font-medium text-amber-800">Foundation Standards Protected</p>
      <p className="text-xs text-amber-700 mt-0.5">
        Foundation standards (priority 10) are ALWAYS included and cannot be deprioritized.
        Profiles adjust delivery ordering, not applicability — all binding standards still apply.
      </p>
    </div>
  </div>
);

/** Tag input component for keyword/category arrays */
const TagInput: React.FC<{
  label: string;
  value: string[];
  onChange: (tags: string[]) => void;
  suggestions?: readonly string[];
  placeholder?: string;
  helpText?: string;
}> = ({ label, value, onChange, suggestions, placeholder, helpText }) => {
  const [inputValue, setInputValue] = useState('');

  const addTag = (tag: string) => {
    const trimmed = tag.trim().toLowerCase();
    if (trimmed && !value.includes(trimmed)) {
      onChange([...value, trimmed]);
    }
    setInputValue('');
  };

  const removeTag = (tag: string) => {
    onChange(value.filter(t => t !== tag));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag(inputValue);
    }
    if (e.key === 'Backspace' && !inputValue && value.length > 0) {
      removeTag(value[value.length - 1]);
    }
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <div className="flex flex-wrap gap-1.5 p-2 border border-gray-300 rounded-lg bg-white min-h-[38px] focus-within:ring-2 focus-within:ring-indigo-500 focus-within:border-indigo-500">
        {value.map(tag => (
          <span
            key={tag}
            className="inline-flex items-center bg-indigo-100 text-indigo-800 text-xs px-2 py-0.5 rounded-full"
          >
            {tag}
            <button
              type="button"
              onClick={() => removeTag(tag)}
              className="ml-1 text-indigo-600 hover:text-indigo-900"
            >
              <FiX className="h-3 w-3" />
            </button>
          </span>
        ))}
        <input
          type="text"
          value={inputValue}
          onChange={e => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={() => inputValue && addTag(inputValue)}
          placeholder={value.length === 0 ? placeholder : ''}
          className="flex-1 min-w-[120px] text-sm border-0 focus:ring-0 focus:outline-none p-0"
          list={suggestions ? `${label}-suggestions` : undefined}
        />
        {suggestions && (
          <datalist id={`${label}-suggestions`}>
            {suggestions.filter(s => !value.includes(s)).map(s => (
              <option key={s} value={s} />
            ))}
          </datalist>
        )}
      </div>
      {helpText && <p className="mt-1 text-xs text-gray-500">{helpText}</p>}
    </div>
  );
};

/** Priority overrides editor (key-value pairs) */
const PriorityOverridesEditor: React.FC<{
  value: Record<string, number>;
  onChange: (overrides: Record<string, number>) => void;
}> = ({ value, onChange }) => {
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('');

  const addOverride = () => {
    if (newKey.trim() && newValue) {
      const numValue = parseInt(newValue, 10);
      if (!isNaN(numValue) && numValue >= 1 && numValue <= 30) {
        onChange({ ...value, [newKey.trim()]: numValue });
        setNewKey('');
        setNewValue('');
      }
    }
  };

  const removeOverride = (key: string) => {
    const updated = { ...value };
    delete updated[key];
    onChange(updated);
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">Priority Overrides</label>
      <p className="text-xs text-gray-500 mb-2">
        Adjust priority ordering for specific standard IDs. Lower number = higher priority.
        Foundation standards (priority 10) cannot be set higher than 10.
      </p>

      {/* Existing overrides */}
      {Object.keys(value).length > 0 && (
        <div className="space-y-1 mb-2">
          {Object.entries(value).map(([key, val]) => (
            <div key={key} className="flex items-center space-x-2 bg-gray-50 rounded px-2 py-1">
              <code className="text-xs text-gray-700 flex-1 truncate">{key}</code>
              <span className="text-xs font-medium text-gray-900">Priority: {val}</span>
              {val > 10 && (
                <FiAlertTriangle className="h-3.5 w-3.5 text-amber-500" title="Cannot deprioritize Foundation standards" />
              )}
              <button
                type="button"
                onClick={() => removeOverride(key)}
                className="text-red-500 hover:text-red-700"
              >
                <FiX className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Add new override */}
      <div className="flex items-center space-x-2">
        <input
          type="text"
          value={newKey}
          onChange={e => setNewKey(e.target.value)}
          placeholder="Standard ID"
          className="flex-1 text-sm border border-gray-300 rounded px-2 py-1 focus:ring-indigo-500 focus:border-indigo-500"
        />
        <input
          type="number"
          value={newValue}
          onChange={e => setNewValue(e.target.value)}
          placeholder="Priority"
          min={1}
          max={30}
          className="w-24 text-sm border border-gray-300 rounded px-2 py-1 focus:ring-indigo-500 focus:border-indigo-500"
        />
        <button
          type="button"
          onClick={addOverride}
          disabled={!newKey.trim() || !newValue}
          className="text-sm bg-indigo-600 text-white px-3 py-1 rounded hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
        >
          Add
        </button>
      </div>
    </div>
  );
};

/** Tech stack editor (JSON key-value pairs) */
const TechStackEditor: React.FC<{
  value: Record<string, any>;
  onChange: (stack: Record<string, any>) => void;
}> = ({ value, onChange }) => {
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('');

  const addEntry = () => {
    if (newKey.trim() && newValue.trim()) {
      onChange({ ...value, [newKey.trim()]: newValue.trim() });
      setNewKey('');
      setNewValue('');
    }
  };

  const removeEntry = (key: string) => {
    const updated = { ...value };
    delete updated[key];
    onChange(updated);
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">Tech Stack</label>
      <p className="text-xs text-gray-500 mb-2">
        Define the tech stack this profile is optimized for (e.g., backend: Lambda, frontend: React).
      </p>

      {Object.keys(value).length > 0 && (
        <div className="space-y-1 mb-2">
          {Object.entries(value).map(([key, val]) => (
            <div key={key} className="flex items-center space-x-2 bg-gray-50 rounded px-2 py-1">
              <span className="text-xs font-medium text-gray-700">{key}:</span>
              <span className="text-xs text-gray-600 flex-1 truncate">{String(val)}</span>
              <button
                type="button"
                onClick={() => removeEntry(key)}
                className="text-red-500 hover:text-red-700"
              >
                <FiX className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center space-x-2">
        <input
          type="text"
          value={newKey}
          onChange={e => setNewKey(e.target.value)}
          placeholder="Key (e.g., backend)"
          className="flex-1 text-sm border border-gray-300 rounded px-2 py-1 focus:ring-indigo-500 focus:border-indigo-500"
        />
        <input
          type="text"
          value={newValue}
          onChange={e => setNewValue(e.target.value)}
          placeholder="Value (e.g., Lambda)"
          className="flex-1 text-sm border border-gray-300 rounded px-2 py-1 focus:ring-indigo-500 focus:border-indigo-500"
        />
        <button
          type="button"
          onClick={addEntry}
          disabled={!newKey.trim() || !newValue.trim()}
          className="text-sm bg-indigo-600 text-white px-3 py-1 rounded hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
        >
          Add
        </button>
      </div>
    </div>
  );
};

// ─── Preview Panel ───────────────────────────────────────────

const PreviewPanel: React.FC<{
  profile: StandardsProfile;
  preview: ProfilePreview | null;
  isLoading: boolean;
  onClose: () => void;
}> = ({ profile, preview, isLoading, onClose }) => (
  <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 flex justify-end">
    <div className="bg-white w-full max-w-lg shadow-xl overflow-y-auto">
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Profile Preview</h2>
            <p className="text-sm text-gray-600">{profile.name}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <FiX className="h-6 w-6" />
          </button>
        </div>

        <FoundationConstraintBanner />

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="inline-block h-8 w-8 border-4 border-indigo-300 border-t-indigo-600 rounded-full animate-spin"></div>
          </div>
        ) : preview ? (
          <div className="mt-6 space-y-6">
            {/* Summary stats */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-indigo-50 rounded-lg p-4">
                <div className="text-xs text-indigo-600 font-medium">Total Standards</div>
                <div className="text-2xl font-bold text-indigo-900 mt-1">{preview.total_standards}</div>
              </div>
              <div className="bg-amber-50 rounded-lg p-4">
                <div className="text-xs text-amber-600 font-medium">Foundation (Protected)</div>
                <div className="text-2xl font-bold text-amber-900 mt-1">{preview.foundation_count}</div>
              </div>
            </div>

            {/* Category breakdown */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Standards by Category</h3>
              <div className="space-y-2">
                {Object.entries(preview.by_category)
                  .sort(([, a], [, b]) => b - a)
                  .map(([category, count]) => (
                    <div key={category} className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <span className="text-sm text-gray-700">{category}</span>
                        {preview.enhanced_categories.includes(category) && (
                          <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">boosted</span>
                        )}
                        {preview.deprioritized_categories.includes(category) && (
                          <span className="text-xs bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded">deprioritized</span>
                        )}
                      </div>
                      <span className="text-sm font-medium text-gray-900">{count}</span>
                    </div>
                  ))}
              </div>
            </div>

            {/* Boosted keywords */}
            {preview.boosted_keywords.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-2">Boosted Keywords</h3>
                <div className="flex flex-wrap gap-1.5">
                  {preview.boosted_keywords.map(keyword => (
                    <span
                      key={keyword}
                      className="inline-flex items-center bg-indigo-100 text-indigo-800 text-xs px-2 py-0.5 rounded-full"
                    >
                      {keyword}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Effectiveness stats */}
            <div className="border-t border-gray-200 pt-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Effectiveness Tracking</h3>
              <div className="grid grid-cols-3 gap-3">
                <div className="text-center">
                  <div className="text-xs text-gray-500">Score</div>
                  <div className="text-lg font-semibold text-gray-900">
                    {formatEffectivenessScore(profile.effectiveness_score)}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-xs text-gray-500">Uses</div>
                  <div className="text-lg font-semibold text-gray-900">{profile.times_used}</div>
                </div>
                <div className="text-center">
                  <div className="text-xs text-gray-500">Success Rate</div>
                  <div className="text-lg font-semibold text-gray-900">
                    {formatSuccessRate(profile.times_used, profile.successful_outcomes)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="mt-6 text-center py-8 text-gray-500 text-sm">
            Preview data not available. Save the profile and try again.
          </div>
        )}
      </div>
    </div>
  </div>
);

// ─── Create/Edit Modal ───────────────────────────────────────

interface ProfileModalProps {
  profile: StandardsProfile | null;  // null = create mode
  onSave: (data: CreateProfileInput | UpdateProfileInput) => Promise<void>;
  onClose: () => void;
  isSaving: boolean;
}

const ProfileModal: React.FC<ProfileModalProps> = ({ profile, onSave, onClose, isSaving }) => {
  const [name, setName] = useState(profile?.name || '');
  const [description, setDescription] = useState(profile?.description || '');
  const [techStack, setTechStack] = useState<Record<string, any>>(profile?.tech_stack || {});
  const [priorityOverrides, setPriorityOverrides] = useState<Record<string, number>>(profile?.priority_overrides || {});
  const [keywordBoosts, setKeywordBoosts] = useState<string[]>(profile?.keyword_boosts || []);
  const [categoryIncludes, setCategoryIncludes] = useState<string[]>(profile?.category_includes || []);
  const [categoryDeprioritizes, setCategoryDeprioritizes] = useState<string[]>(profile?.category_deprioritizes || []);
  const [status, setStatus] = useState<'draft' | 'active' | 'archived'>(profile?.status || 'draft');
  const [builderMode, setBuilderMode] = useState<'form' | 'visual'>('form');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const data: CreateProfileInput = {
      name,
      description: description || undefined,
      tech_stack: Object.keys(techStack).length > 0 ? techStack : undefined,
      priority_overrides: Object.keys(priorityOverrides).length > 0 ? priorityOverrides : undefined,
      keyword_boosts: keywordBoosts.length > 0 ? keywordBoosts : undefined,
      category_includes: categoryIncludes.length > 0 ? categoryIncludes : undefined,
      category_deprioritizes: categoryDeprioritizes.length > 0 ? categoryDeprioritizes : undefined,
      status,
    };
    await onSave(data);
  };

  const isCreate = profile === null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-xl">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                {isCreate ? 'Create Standards Profile' : 'Edit Standards Profile'}
              </h2>
              <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
                <FiX className="h-6 w-6" />
              </button>
            </div>
            {/* Mode toggle */}
            <div className="flex gap-1 mt-3 bg-gray-100 rounded-lg p-0.5 w-fit">
              <button
                type="button"
                onClick={() => setBuilderMode('form')}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors
                  ${builderMode === 'form' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
              >
                <FiList className="h-3.5 w-3.5" />
                Form
              </button>
              <button
                type="button"
                onClick={() => setBuilderMode('visual')}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors
                  ${builderMode === 'visual' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
              >
                <FiGrid className="h-3.5 w-3.5" />
                Visual Builder
              </button>
            </div>
          </div>

          <div className="px-6 py-4 space-y-6">
            <FoundationConstraintBanner />

            {/* Name */}
            <div>
              <label htmlFor="profile-name" className="block text-sm font-medium text-gray-700 mb-1">
                Profile Name <span className="text-red-500">*</span>
              </label>
              <input
                id="profile-name"
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                required
                maxLength={100}
                placeholder="e.g., React + Lambda Full Stack"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>

            {/* Description */}
            <div>
              <label htmlFor="profile-description" className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                id="profile-description"
                value={description}
                onChange={e => setDescription(e.target.value)}
                rows={3}
                placeholder="Describe what this profile optimizes for..."
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>

            {/* Status */}
            <div>
              <label htmlFor="profile-status" className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                id="profile-status"
                value={status}
                onChange={e => setStatus(e.target.value as 'draft' | 'active' | 'archived')}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="draft">Draft</option>
                <option value="active">Active</option>
                <option value="archived">Archived</option>
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Only active profiles can be used by the StandardsFetchingService.
              </p>
            </div>

            {/* Tech Stack */}
            <TechStackEditor value={techStack} onChange={setTechStack} />

            {/* Category & Priority Configuration */}
            {builderMode === 'form' ? (
              <>
                {/* Category Includes */}
                <TagInput
                  label="Category Boosts"
                  value={categoryIncludes}
                  onChange={setCategoryIncludes}
                  suggestions={STANDARD_CATEGORIES}
                  placeholder="Type a category and press Enter..."
                  helpText="Additional categories to prioritize when this profile is active."
                />

                {/* Category Deprioritizes */}
                <TagInput
                  label="Category Deprioritizes"
                  value={categoryDeprioritizes}
                  onChange={setCategoryDeprioritizes}
                  suggestions={STANDARD_CATEGORIES}
                  placeholder="Type a category and press Enter..."
                  helpText="Categories to fetch later in ordering (NOT excluded). All standards still apply."
                />

                {/* Priority Overrides */}
                <PriorityOverridesEditor
                  value={priorityOverrides}
                  onChange={setPriorityOverrides}
                />
              </>
            ) : (
              <ProfileBuilder
                categoryIncludes={categoryIncludes}
                categoryDeprioritizes={categoryDeprioritizes}
                priorityOverrides={priorityOverrides}
                onChange={(updates) => {
                  if (updates.category_includes) setCategoryIncludes(updates.category_includes);
                  if (updates.category_deprioritizes) setCategoryDeprioritizes(updates.category_deprioritizes);
                  if (updates.priority_overrides) setPriorityOverrides(updates.priority_overrides);
                }}
              />
            )}

            {/* Keyword Boosts (shown in both modes) */}
            <TagInput
              label="Keyword Boosts"
              value={keywordBoosts}
              onChange={setKeywordBoosts}
              placeholder="Type a keyword and press Enter..."
              helpText="Additional keywords to match when this profile is active, boosting relevance of matching standards."
            />

            {/* Profile Simulation (shown in both modes) */}
            <ProfileSimulator
              categoryIncludes={categoryIncludes}
              categoryDeprioritizes={categoryDeprioritizes}
              priorityOverrides={priorityOverrides}
              currentEffectiveness={profile?.effectiveness_score}
            />
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 rounded-b-xl flex items-center justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!name.trim() || isSaving}
              className="px-4 py-2 text-sm text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {isSaving && (
                <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              )}
              <span>{isCreate ? 'Create Profile' : 'Save Changes'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Delete Confirmation Modal ───────────────────────────────

const DeleteConfirmModal: React.FC<{
  profile: StandardsProfile;
  onConfirm: () => void;
  onCancel: () => void;
  isDeleting: boolean;
}> = ({ profile, onConfirm, onCancel, isDeleting }) => (
  <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 flex items-center justify-center p-4">
    <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
      <div className="flex items-start space-x-4">
        <div className="flex-shrink-0 bg-red-100 rounded-full p-2">
          <FiTrash2 className="h-6 w-6 text-red-600" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Delete Profile</h3>
          <p className="mt-2 text-sm text-gray-600">
            Are you sure you want to delete <strong>{profile.name}</strong>?
            This action cannot be undone.
          </p>
          {profile.times_used > 0 && (
            <p className="mt-2 text-sm text-amber-600">
              This profile has been used {profile.times_used} time{profile.times_used !== 1 ? 's' : ''}.
            </p>
          )}
        </div>
      </div>
      <div className="mt-6 flex justify-end space-x-3">
        <button
          onClick={onCancel}
          className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          disabled={isDeleting}
          className="px-4 py-2 text-sm text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center space-x-2"
        >
          {isDeleting && (
            <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
          )}
          <span>Delete</span>
        </button>
      </div>
    </div>
  </div>
);

// ─── Main Page Component ─────────────────────────────────────

const StandardsProfilesPage: React.FC = () => {
  const { Client_ID, Auth_Status } = useStatus();

  // State
  const [profiles, setProfiles] = useState<StandardsProfile[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingProfile, setEditingProfile] = useState<StandardsProfile | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Delete state
  const [deletingProfile, setDeletingProfile] = useState<StandardsProfile | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Preview state
  const [previewingProfile, setPreviewingProfile] = useState<StandardsProfile | null>(null);
  const [previewData, setPreviewData] = useState<ProfilePreview | null>(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);

  // Filter state
  const [statusFilter, setStatusFilter] = useState<string>('');

  const effectiveClientId = Client_ID || localStorage.getItem('selectedClientId') || '';

  // ─── Data Fetching ───────────────────────────────────────

  const fetchProfiles = useCallback(async () => {
    if (!Auth_Status) return;

    setIsLoading(true);
    setError('');

    try {
      const response = await listProfiles(effectiveClientId);

      if (!isSuccessResponse(response)) {
        throw new Error(response.message || 'Failed to fetch profiles');
      }

      const profilesList = response.data?.Records || [];
      setProfiles(profilesList);
    } catch (err) {
      console.error('Error fetching profiles:', err);
      setError(err instanceof Error ? err.message : 'Failed to load profiles');
    } finally {
      setIsLoading(false);
    }
  }, [Auth_Status, effectiveClientId]);

  useEffect(() => {
    fetchProfiles();
  }, [fetchProfiles]);

  // ─── Actions ─────────────────────────────────────────────

  const handleCreate = async (data: CreateProfileInput | UpdateProfileInput) => {
    setIsSaving(true);
    try {
      const response = await createProfile(data as CreateProfileInput);
      if (!isSuccessResponse(response)) {
        throw new Error(response.message || 'Failed to create profile');
      }
      setShowCreateModal(false);
      await fetchProfiles();
    } catch (err) {
      console.error('Error creating profile:', err);
      setError(err instanceof Error ? err.message : 'Failed to create profile');
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdate = async (data: CreateProfileInput | UpdateProfileInput) => {
    if (!editingProfile) return;
    setIsSaving(true);
    try {
      const response = await updateProfile(editingProfile.profile_id, data as UpdateProfileInput);
      if (!isSuccessResponse(response)) {
        throw new Error(response.message || 'Failed to update profile');
      }
      setEditingProfile(null);
      await fetchProfiles();
    } catch (err) {
      console.error('Error updating profile:', err);
      setError(err instanceof Error ? err.message : 'Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingProfile) return;
    setIsDeleting(true);
    try {
      const response = await deleteProfile(deletingProfile.profile_id);
      if (!isSuccessResponse(response)) {
        throw new Error(response.message || 'Failed to delete profile');
      }
      setDeletingProfile(null);
      await fetchProfiles();
    } catch (err) {
      console.error('Error deleting profile:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete profile');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleToggleStatus = async (profile: StandardsProfile) => {
    const newStatus = profile.status === 'active' ? 'draft' : 'active';
    try {
      const response = await updateProfile(profile.profile_id, { status: newStatus });
      if (!isSuccessResponse(response)) {
        throw new Error(response.message || 'Failed to update status');
      }
      await fetchProfiles();
    } catch (err) {
      console.error('Error toggling status:', err);
      setError(err instanceof Error ? err.message : 'Failed to update status');
    }
  };

  const handlePreview = async (profile: StandardsProfile) => {
    setPreviewingProfile(profile);
    setPreviewData(null);
    setIsPreviewLoading(true);
    try {
      const response = await previewProfile(profile.profile_id);
      if (isSuccessResponse(response)) {
        // Preview returns a single record, not an array
        const records = response.data?.Records;
        if (Array.isArray(records) && records.length > 0) {
          setPreviewData(records[0] as unknown as ProfilePreview);
        } else {
          setPreviewData(records as unknown as ProfilePreview);
        }
      }
    } catch (err) {
      console.error('Error loading preview:', err);
    } finally {
      setIsPreviewLoading(false);
    }
  };

  // ─── Filtered Data ───────────────────────────────────────

  const filteredProfiles = statusFilter
    ? profiles.filter(p => p.status === statusFilter)
    : profiles;

  const stats = {
    total: profiles.length,
    active: profiles.filter(p => p.status === 'active').length,
    draft: profiles.filter(p => p.status === 'draft').length,
    archived: profiles.filter(p => p.status === 'archived').length,
  };

  // ─── Render ──────────────────────────────────────────────

  return (
    <div className="p-6 h-full overflow-auto">
      {/* Page Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Standards Profiles</h1>
            <p className="text-gray-600 mt-1">
              Manage delivery priority profiles for the StandardsFetchingService
            </p>
          </div>

          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center space-x-2 bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors"
          >
            <FiPlus className="h-5 w-5" />
            <span>Create Profile</span>
          </button>
        </div>

        {/* Statistics */}
        <div className="grid grid-cols-4 gap-3 mb-6">
          <div className="bg-white rounded-lg border border-gray-200 p-3">
            <div className="text-xs text-gray-600 font-medium">Total</div>
            <div className="text-2xl font-bold text-gray-900 mt-1">{stats.total}</div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-3">
            <div className="text-xs text-gray-600 font-medium">Active</div>
            <div className="text-2xl font-bold text-green-600 mt-1">{stats.active}</div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-3">
            <div className="text-xs text-gray-600 font-medium">Draft</div>
            <div className="text-2xl font-bold text-yellow-600 mt-1">{stats.draft}</div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-3">
            <div className="text-xs text-gray-600 font-medium">Archived</div>
            <div className="text-2xl font-bold text-gray-500 mt-1">{stats.archived}</div>
          </div>
        </div>

        {/* Foundation Constraint Banner */}
        <div className="mb-4">
          <FoundationConstraintBanner />
        </div>

        {/* Filters */}
        <div className="flex items-center space-x-3">
          <button
            onClick={() => fetchProfiles()}
            className="flex items-center space-x-1 text-gray-600 hover:text-gray-900 px-3 py-2 rounded-lg border border-gray-200 hover:border-gray-300 transition-colors"
          >
            <FiRefreshCw className="h-4 w-4" />
            <span className="text-sm">Refresh</span>
          </button>

          <div className="flex items-center space-x-2 bg-white rounded-lg border border-gray-200 px-3 py-2">
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              className="text-sm bg-transparent border-0 focus:outline-none focus:ring-0"
            >
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="draft">Draft</option>
              <option value="archived">Archived</option>
            </select>
          </div>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center justify-between">
          <div className="text-sm text-red-800">
            <strong>Error:</strong> {error}
          </div>
          <button onClick={() => setError('')} className="text-red-600 hover:text-red-800">
            <FiX className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="inline-block h-8 w-8 border-4 border-indigo-300 border-t-indigo-600 rounded-full animate-spin mb-2"></div>
            <p className="text-gray-600">Loading profiles...</p>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && filteredProfiles.length === 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <FiShield className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-gray-900 mb-1">No Profiles Found</h3>
          <p className="text-gray-600 text-sm mb-4">
            {profiles.length === 0
              ? 'Create a profile to tune standards delivery priorities for specific tech stacks.'
              : 'No profiles match your current filter.'}
          </p>
          {profiles.length === 0 && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center space-x-2 bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700"
            >
              <FiPlus className="h-4 w-4" />
              <span>Create First Profile</span>
            </button>
          )}
        </div>
      )}

      {/* Profiles Table */}
      {!isLoading && filteredProfiles.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Profile
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Category Boosts
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Effectiveness
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Usage
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredProfiles.map(profile => (
                <tr key={profile.profile_id} className="hover:bg-gray-50">
                  {/* Profile name and description */}
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">{profile.name}</div>
                    {profile.description && (
                      <div className="text-xs text-gray-500 mt-0.5 line-clamp-1">{profile.description}</div>
                    )}
                    {Object.keys(profile.tech_stack || {}).length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {Object.entries(profile.tech_stack).map(([key, val]) => (
                          <span key={key} className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">
                            {key}: {String(val)}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>

                  {/* Status with toggle */}
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-2">
                      <StatusBadge status={profile.status} />
                      {profile.status !== 'archived' && (
                        <button
                          onClick={() => handleToggleStatus(profile)}
                          className="text-xs text-indigo-600 hover:text-indigo-800 underline"
                          title={profile.status === 'active' ? 'Deactivate' : 'Activate'}
                        >
                          {profile.status === 'active' ? 'Deactivate' : 'Activate'}
                        </button>
                      )}
                    </div>
                  </td>

                  {/* Category boosts */}
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1">
                      {(profile.category_includes || []).map(cat => (
                        <span key={cat} className="inline-flex items-center bg-green-100 text-green-800 text-xs px-1.5 py-0.5 rounded">
                          {cat}
                        </span>
                      ))}
                      {(profile.category_deprioritizes || []).map(cat => (
                        <span key={cat} className="inline-flex items-center bg-orange-100 text-orange-700 text-xs px-1.5 py-0.5 rounded line-through">
                          {cat}
                        </span>
                      ))}
                      {(profile.category_includes || []).length === 0 && (profile.category_deprioritizes || []).length === 0 && (
                        <span className="text-xs text-gray-400">None</span>
                      )}
                    </div>
                  </td>

                  {/* Effectiveness */}
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">
                      {formatEffectivenessScore(profile.effectiveness_score)}
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1">
                      <div
                        className="bg-indigo-600 h-1.5 rounded-full"
                        style={{ width: `${Math.min(profile.effectiveness_score * 100, 100)}%` }}
                      ></div>
                    </div>
                  </td>

                  {/* Usage */}
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">{profile.times_used} uses</div>
                    <div className="text-xs text-gray-500">
                      {formatSuccessRate(profile.times_used, profile.successful_outcomes)} success
                    </div>
                  </td>

                  {/* Actions */}
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => handlePreview(profile)}
                        className="p-1.5 text-gray-400 hover:text-indigo-600 rounded hover:bg-indigo-50"
                        title="Preview delivery impact"
                      >
                        <FiEye className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => setEditingProfile(profile)}
                        className="p-1.5 text-gray-400 hover:text-indigo-600 rounded hover:bg-indigo-50"
                        title="Edit profile"
                      >
                        <FiEdit2 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => setDeletingProfile(profile)}
                        className="p-1.5 text-gray-400 hover:text-red-600 rounded hover:bg-red-50"
                        title="Delete profile"
                      >
                        <FiTrash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modals */}
      {showCreateModal && (
        <ProfileModal
          profile={null}
          onSave={handleCreate}
          onClose={() => setShowCreateModal(false)}
          isSaving={isSaving}
        />
      )}

      {editingProfile && (
        <ProfileModal
          profile={editingProfile}
          onSave={handleUpdate}
          onClose={() => setEditingProfile(null)}
          isSaving={isSaving}
        />
      )}

      {deletingProfile && (
        <DeleteConfirmModal
          profile={deletingProfile}
          onConfirm={handleDelete}
          onCancel={() => setDeletingProfile(null)}
          isDeleting={isDeleting}
        />
      )}

      {previewingProfile && (
        <PreviewPanel
          profile={previewingProfile}
          preview={previewData}
          isLoading={isPreviewLoading}
          onClose={() => {
            setPreviewingProfile(null);
            setPreviewData(null);
          }}
        />
      )}
    </div>
  );
};

export default StandardsProfilesPage;
